import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFetchTaskHistory } from '../state/data/task';
import {
  anchorTaskDate, hasAppointmentState, completionLineOf, taskDisplayTitle,
} from '../logic/task';

/* ── Visual constants — kept in sync with TaskHistoryModal ─────── */

const PRIO_COLORS = {
  urgent: { bg: 'var(--wi-background-bad)',      color: 'var(--wi-status-bad)' },
  high:   { bg: 'var(--wi-background-moderate)', color: 'var(--wi-text-primary-moderate)' },
  normal: { bg: 'var(--wi-background-primary)',  color: 'var(--wi-text-secondary)' },
  low:    { bg: 'var(--wi-background-good)',     color: 'var(--wi-text-primary-good)' },
};

const STATUS_STYLES = {
  completed:   { bg: 'var(--wi-background-good)',      color: 'var(--wi-text-primary-good)',     label: 'Completed' },
  in_progress: { bg: 'var(--wi-background-info)',      color: 'var(--wi-action-primary)',        label: 'In Progress' },
  pending:     { bg: 'var(--wi-background-moderate)',  color: 'var(--wi-text-primary-moderate)', label: 'Pending' },
  scheduled:   { bg: 'var(--wi-background-primary)',   color: 'var(--wi-text-secondary)',        label: 'Scheduled' },
  overdue:     { bg: 'var(--wi-background-bad)',       color: 'var(--wi-status-bad)',            label: 'Overdue' },
  cancelled:   { bg: 'var(--wi-background-primary)',   color: 'var(--wi-text-secondary)',        label: 'Cancelled' },
  missed:      { bg: 'var(--wi-background-bad)',       color: 'var(--wi-text-primary-bad)',      label: 'Missed' },
};

const DOMAIN_COLORS = {
  bp: '#1F4AC2', weight: '#059669', lab: '#7C3AED',
  medication: '#D97706', reporting: '#0284C7',
  compliance: '#BE185D', outreach: '#6B7280', device: '#374151',
  education: '#0891B2',
};

const ROLE_INITIAL = { care_coordinator: 'C', clinician: 'L', patient: 'P', system: 'S', md: 'L', pharmacist: 'L', rn: 'L', np: 'L', care_team: 'L', operations: 'C', admin: 'C', ops: 'C', operator: 'C' };
const ROLE_LABEL   = { care_coordinator: 'Care Coordinator', clinician: 'Clinician', patient: 'Patient', system: 'System', md: 'Clinician', pharmacist: 'Clinician', rn: 'Clinician', np: 'Clinician', care_team: 'Clinician', operations: 'Care Coordinator', admin: 'Care Coordinator', ops: 'Care Coordinator', operator: 'Care Coordinator' };
const ASSIGNED_LABEL = { staff: 'Care team', patient: 'Patient', provider: 'Provider', system: 'System' };
const PRIO_LABEL = { urgent: 'Urgent', high: 'High', normal: 'Normal', low: 'Low' };

const OPEN_STATUSES = new Set(['pending', 'in_progress', 'overdue', 'scheduled']);

/* ── Date helpers (no external lib) ────────────────────────────── */

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function startOfWeek(d) {
  // Monday-based week — care team operates on weekdays.
  const x = startOfDay(d);
  const weekday = x.getDay(); // 0 Sun .. 6 Sat
  const delta = (weekday + 6) % 7; // Mon=0, Sun=6
  return addDays(x, -delta);
}
function startOfMonth(d) {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
}
function endOfMonth(d) {
  const x = startOfMonth(d);
  x.setMonth(x.getMonth() + 1);
  x.setDate(0);
  return x;
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
}
function dateKey(d) {
  // YYYY-MM-DD in local time — used as Map key.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function formatMonthYear(d) {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
function formatWeekRange(start) {
  const end = addDays(start, 6);
  const sameMonth = start.getMonth() === end.getMonth();
  const sameYear = start.getFullYear() === end.getFullYear();
  const startFmt = start.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });
  const endFmt = end.toLocaleDateString('en-US', {
    month: sameMonth ? undefined : 'short', day: 'numeric', year: 'numeric',
  });
  return `${startFmt} – ${endFmt}`;
}
function formatDayLong(d) {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
function formatRelative(dateStr) {
  if (!dateStr) return null;
  const then = new Date(dateStr);
  const now = new Date();
  const diff = now - then;
  if (diff < 0) return 'just now';
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/* Anchor logic lives in logic/task.ts (anchorTaskDate) — unit-tested there. */

/* ── Small UI atoms ───────────────────────────────────────────── */

function Pill({ bg, color, children, style }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
      background: bg, color, textTransform: 'uppercase', letterSpacing: '0.02em',
      whiteSpace: 'nowrap', ...style,
    }}>
      {children}
    </span>
  );
}

/* ── Dropdown menus for filters ──────────────────────────────── */

const dropdownTriggerStyle = {
  background: 'var(--wi-background-secondary)',
  border: '1px solid var(--wi-divider)',
  borderRadius: 6,
  padding: '5px 10px',
  fontSize: 12,
  fontFamily: 'var(--wi-font-family)',
  color: 'var(--wi-text-primary)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  whiteSpace: 'nowrap',
};

const dropdownPanelStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  background: 'var(--wi-background-secondary)',
  border: '1px solid var(--wi-divider)',
  borderRadius: 6,
  padding: 4,
  minWidth: 180,
  zIndex: 1500,
  boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
  fontFamily: 'var(--wi-font-family)',
};

const dropdownRowStyle = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '6px 8px', fontSize: 12,
  cursor: 'pointer', borderRadius: 4,
  color: 'var(--wi-text-primary)',
  whiteSpace: 'nowrap',
};

function useClickOutside(ref, onClose, active) {
  useEffect(() => {
    if (!active) return;
    function handle(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [active, onClose, ref]);
}

function SingleSelect({ label, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false), open);
  const current = options.find(o => o.value === value);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={dropdownTriggerStyle}>
        <span style={{ color: 'var(--wi-text-secondary)', fontWeight: 500 }}>{label}:</span>
        <span style={{ fontWeight: 500 }}>{current?.label || '—'}</span>
        <span style={{ fontSize: 9, color: 'var(--wi-text-placeholder)' }}>▾</span>
      </button>
      {open && (
        <div style={dropdownPanelStyle}>
          {options.map(o => (
            <div
              key={o.value || 'all'}
              onClick={() => { onChange(o.value); setOpen(false); }}
              style={{
                ...dropdownRowStyle,
                background: value === o.value ? 'var(--wi-background-info)' : 'transparent',
                fontWeight: value === o.value ? 600 : 400,
              }}
              onMouseEnter={e => { if (value !== o.value) e.currentTarget.style.background = 'var(--wi-background-primary)'; }}
              onMouseLeave={e => { if (value !== o.value) e.currentTarget.style.background = 'transparent'; }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useClickOutside(ref, () => setOpen(false), open);

  const allSelected = options.length > 0 && options.every(o => selected.has(o.value));
  const noneSelected = selected.size === 0;
  const summary = allSelected
    ? 'All'
    : noneSelected
      ? 'None'
      : selected.size === 1
        ? (options.find(o => selected.has(o.value))?.label || '1')
        : `${selected.size} of ${options.length}`;

  function toggleAll() {
    onChange(allSelected ? new Set() : new Set(options.map(o => o.value)));
  }
  function toggle(val) {
    const next = new Set(selected);
    if (next.has(val)) next.delete(val); else next.add(val);
    onChange(next);
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={dropdownTriggerStyle}>
        <span style={{ color: 'var(--wi-text-secondary)', fontWeight: 500 }}>{label}:</span>
        <span style={{ fontWeight: 500 }}>{summary}</span>
        <span style={{ fontSize: 9, color: 'var(--wi-text-placeholder)' }}>▾</span>
      </button>
      {open && (
        <div style={dropdownPanelStyle}>
          {options.length > 1 && (
            <div
              onClick={toggleAll}
              style={{
                ...dropdownRowStyle,
                color: 'var(--wi-action-primary)',
                fontSize: 11, fontWeight: 600,
                borderBottom: '1px solid var(--wi-divider)',
                marginBottom: 2,
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--wi-background-primary)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </div>
          )}
          {options.map(o => {
            const checked = selected.has(o.value);
            return (
              <label
                key={o.value}
                style={dropdownRowStyle}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--wi-background-primary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(o.value)}
                  style={{ cursor: 'pointer', margin: 0 }}
                />
                {o.swatch && (
                  <span style={{
                    width: 10, height: 10, borderRadius: 2,
                    background: o.swatch, flexShrink: 0,
                  }} />
                )}
                <span>{o.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function HighOnlyToggle({ active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title="Only urgent and high priority tasks"
      style={{
        background: active ? 'var(--wi-status-bad)' : 'var(--wi-background-secondary)',
        color: active ? '#fff' : 'var(--wi-text-secondary)',
        border: `1px solid ${active ? 'var(--wi-status-bad)' : 'var(--wi-divider)'}`,
        borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 500,
        cursor: 'pointer', fontFamily: 'var(--wi-font-family)',
        whiteSpace: 'nowrap',
      }}
    >
      High+ only
    </button>
  );
}

/* ── Hover popover ────────────────────────────────────────────── */

function TaskPopover({ task, anchorRect, onClose: _onClose }) {
  // Position: prefer right of the chip; fall back to left or below if it would overflow.
  const POP_W = 320;
  const POP_H_EST = 200;
  const margin = 8;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = anchorRect.right + margin;
  let top  = anchorRect.top;
  if (left + POP_W > vw - 8) left = anchorRect.left - POP_W - margin;
  if (left < 8) left = Math.max(8, anchorRect.left);
  if (top + POP_H_EST > vh - 8) top = Math.max(8, vh - POP_H_EST - 8);
  if (top < 8) top = 8;

  const prio = PRIO_COLORS[task.priority] || PRIO_COLORS.normal;
  const status = STATUS_STYLES[task.status] || STATUS_STYLES.pending;
  const domainColor = DOMAIN_COLORS[task.clinical_domain] || '#6B7280';

  return (
    <div
      role="tooltip"
      onMouseEnter={e => e.stopPropagation()}
      style={{
        position: 'fixed', left, top, width: POP_W, zIndex: 2000,
        background: 'var(--wi-background-secondary)',
        border: '1px solid var(--wi-divider)',
        borderRadius: 8, padding: 12,
        boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
        fontFamily: 'var(--wi-font-family)',
        pointerEvents: 'none',
      }}
    >
      <div style={{
        fontSize: 13, fontWeight: 600, color: 'var(--wi-text-primary)',
        marginBottom: 6, lineHeight: 1.3,
        textDecoration: task.status === 'cancelled' ? 'line-through' : 'none',
      }}>
        {taskDisplayTitle(task)}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        <Pill bg={status.bg} color={status.color}>{status.label}</Pill>
        {task.priority && task.priority !== 'normal' && (
          <Pill bg={prio.bg} color={prio.color}>{PRIO_LABEL[task.priority] || task.priority}</Pill>
        )}
        {task.clinical_domain && (
          <Pill bg={`${domainColor}18`} color={domainColor} style={{ textTransform: 'lowercase' }}>
            {task.clinical_domain}
          </Pill>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 10, rowGap: 4, fontSize: 11, color: 'var(--wi-text-secondary)' }}>
        {task.owner_role && (
          <>
            <span>Owner</span>
            <span style={{ color: 'var(--wi-text-primary)' }}>{ROLE_LABEL[task.owner_role] || task.owner_role}</span>
          </>
        )}
        {task.assigned_to_type && (
          <>
            <span>Assigned</span>
            <span style={{ color: 'var(--wi-text-primary)' }}>{ASSIGNED_LABEL[task.assigned_to_type] || task.assigned_to_type}</span>
          </>
        )}
        {task.available_at && (
          <>
            <span>Available</span>
            <span style={{ color: 'var(--wi-text-primary)' }}>{formatDayLong(new Date(task.available_at))}</span>
          </>
        )}
        {task.due_at && (
          <>
            <span>Due</span>
            <span style={{ color: 'var(--wi-text-primary)' }}>{formatDayLong(new Date(task.due_at))}</span>
          </>
        )}
        {(() => {
          const line = completionLineOf(task);
          if (!line?.at) return null;
          return (
            <>
              <span>{line.verb}</span>
              <span style={{ color: 'var(--wi-text-primary)' }}>
                {formatDayLong(new Date(line.at))}
                {line.by ? ` · ${line.by}` : ''}
              </span>
            </>
          );
        })()}
        {(task.modified ?? task.updated_at) && (
          <>
            <span>Updated</span>
            <span style={{ color: 'var(--wi-text-primary)' }}>{formatRelative(task.modified ?? task.updated_at)}</span>
          </>
        )}
      </div>
      {task.description && (
        <div
          style={{
            marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--wi-divider)',
            fontSize: 11, color: 'var(--wi-text-secondary)', lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {task.description}
        </div>
      )}
    </div>
  );
}

/* ── Event chip ───────────────────────────────────────────────── */

function priorityBorder(priority) {
  switch (priority) {
    case 'urgent': return '3px solid var(--wi-status-bad)';
    case 'high':   return '2px solid var(--wi-text-primary-moderate)';
    case 'normal': return '1px solid var(--wi-divider)';
    case 'low':    return '1px solid var(--wi-background-good)';
    default:       return '1px solid var(--wi-divider)';
  }
}

function TaskChip({ task, dense, onHover, onLeave }) {
  const ref = useRef(null);
  const status = task.status;
  const isFaded = status === 'cancelled' || status === 'missed';
  const isStrike = status === 'completed' || status === 'cancelled';
  const booked = hasAppointmentState(task);
  const domainColor = DOMAIN_COLORS[task.clinical_domain] || '#6B7280';
  const role = ROLE_INITIAL[task.owner_role] || '·';

  const handleEnter = () => {
    if (ref.current) onHover(task, ref.current.getBoundingClientRect());
  };

  return (
    <div
      ref={ref}
      onMouseEnter={handleEnter}
      onFocus={handleEnter}
      onMouseLeave={onLeave}
      onBlur={onLeave}
      tabIndex={0}
      role="button"
      aria-label={`${taskDisplayTitle(task)} — ${status}`}
      style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: dense ? '2px 5px 2px 6px' : '4px 7px 4px 8px',
        background: `${domainColor}1A`,
        borderLeft: priorityBorder(task.priority),
        borderRadius: 3,
        fontSize: dense ? 12 : 13,
        lineHeight: 1.3,
        color: 'var(--wi-text-primary)',
        cursor: 'pointer',
        opacity: isFaded ? 0.55 : 1,
        textDecoration: isStrike ? 'line-through' : 'none',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        outline: 'none',
        minWidth: 0,
      }}
    >
      {status === 'completed' && (
        <span aria-hidden="true" style={{ color: 'var(--wi-status-good)', fontWeight: 700, flexShrink: 0 }}>✓</span>
      )}
      {status === 'in_progress' && (
        /* A pulse is this calendar's strongest "live right now" signal. An Elation-booked visit
           carries status in_progress but may be days away, so it gets a static dot instead. */
        <span aria-hidden="true" style={{
          width: 6, height: 6, borderRadius: '50%',
          background: 'var(--wi-action-primary)',
          flexShrink: 0,
          ...(booked ? {} : { animation: 'taskcal-pulse 1.4s ease-in-out infinite' }),
        }} />
      )}
      {(status === 'overdue' || status === 'missed') && (
        <span aria-hidden="true" style={{ color: 'var(--wi-status-bad)', fontWeight: 700, flexShrink: 0 }}>!</span>
      )}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, minWidth: 0 }}>
        {taskDisplayTitle(task)}
      </span>
      {role !== '·' && (
        <span aria-hidden="true" style={{
          fontSize: 11, fontWeight: 700,
          padding: '1px 4px', minWidth: 14, textAlign: 'center',
          color: 'var(--wi-text-secondary)',
          background: 'var(--wi-background-primary)',
          borderRadius: 3, flexShrink: 0,
        }}>
          {role}
        </span>
      )}
    </div>
  );
}

/* ── Day cell — month view ────────────────────────────────────── */

function MonthCell({ date, tasks, isToday, isCurrentMonth, isWeekend, onHover, onLeave, onShowAll }) {
  const MAX_VISIBLE = 3;
  const overflow = tasks.length - MAX_VISIBLE;
  const visible = overflow > 0 ? tasks.slice(0, MAX_VISIBLE) : tasks;

  return (
    <div
      role="gridcell"
      style={{
        background: isToday
          ? 'rgba(22, 56, 166, 0.04)'
          : isCurrentMonth
            ? 'var(--wi-background-secondary)'
            : 'var(--wi-background-primary)',
        border: isToday ? '1px solid var(--wi-action-primary)' : '1px solid var(--wi-divider)',
        borderRadius: 4,
        padding: 4,
        height: '100%',
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column', gap: 2,
        opacity: isCurrentMonth ? 1 : 0.55,
        position: 'relative',
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 13, fontWeight: isToday ? 700 : 500,
        color: isToday ? 'var(--wi-action-primary)' : (isWeekend ? 'var(--wi-text-placeholder)' : 'var(--wi-text-secondary)'),
        marginBottom: 2, padding: '0 2px',
      }}>
        <span>{date.getDate()}</span>
        {tasks.length > 0 && (
          <span style={{ fontSize: 11, color: 'var(--wi-text-placeholder)' }}>{tasks.length}</span>
        )}
      </div>
      {visible.map(t => (
        <TaskChip key={t.task_id} task={t} dense onHover={onHover} onLeave={onLeave} />
      ))}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => onShowAll(date, tasks)}
          style={{
            background: 'none', border: 'none', padding: '2px 4px',
            color: 'var(--wi-action-primary)', fontSize: 12, fontWeight: 500,
            cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--wi-font-family)',
          }}
        >
          +{overflow} more
        </button>
      )}
    </div>
  );
}

/* ── Day cell — week view ─────────────────────────────────────── */

function WeekCell({ date, tasks, isToday, isWeekend, onHover, onLeave }) {
  return (
    <div
      role="gridcell"
      style={{
        background: isToday ? 'rgba(22, 56, 166, 0.04)' : 'var(--wi-background-secondary)',
        border: isToday ? '1px solid var(--wi-action-primary)' : '1px solid var(--wi-divider)',
        borderRadius: 6,
        padding: 8,
        display: 'flex', flexDirection: 'column', gap: 4,
        minHeight: 380,
      }}
    >
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: 6, paddingBottom: 6,
        borderBottom: '1px solid var(--wi-divider)',
      }}>
        <div>
          <div style={{
            fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
            color: isWeekend ? 'var(--wi-text-placeholder)' : 'var(--wi-text-secondary)',
          }}>
            {date.toLocaleDateString('en-US', { weekday: 'short' })}
          </div>
          <div style={{
            fontSize: 18, fontWeight: 700,
            color: isToday ? 'var(--wi-action-primary)' : 'var(--wi-text-primary)',
          }}>
            {date.getDate()}
          </div>
        </div>
        {tasks.length > 0 && (
          <span style={{ fontSize: 10, color: 'var(--wi-text-placeholder)' }}>
            {tasks.length}
          </span>
        )}
      </div>
      {tasks.length === 0 && (
        <div style={{ fontSize: 10, color: 'var(--wi-text-placeholder)', textAlign: 'center', padding: '12px 0' }}>
          —
        </div>
      )}
      {tasks.map(t => (
        <TaskChip key={t.task_id} task={t} dense={false} onHover={onHover} onLeave={onLeave} />
      ))}
    </div>
  );
}

/* ── Day overflow popover (when "+N more" is clicked) ─────────── */

function DayOverflowPopover({ date, tasks, onClose, onHover, onLeave }) {
  return (
    <div
      role="dialog"
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed', inset: 0, zIndex: 1900,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.25)',
      }}
    >
      <div
        style={{
          background: 'var(--wi-background-secondary)',
          borderRadius: 8, padding: 16, width: 360, maxHeight: '70vh', overflow: 'auto',
          boxShadow: '0 14px 40px rgba(0,0,0,0.25)',
          fontFamily: 'var(--wi-font-family)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <strong style={{ fontSize: 14 }}>{formatDayLong(date)}</strong>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 18, lineHeight: 1, color: 'var(--wi-text-secondary)',
            }}
          >×</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {tasks.map(t => (
            <TaskChip key={t.task_id} task={t} dense={false} onHover={onHover} onLeave={onLeave} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Filter bar ───────────────────────────────────────────────── */

const ALL_ROLES = ['care_coordinator', 'clinician', 'patient', 'system'];
const DEFAULT_STATUSES = ['active', 'completed'];

const ASSIGNEE_OPTIONS = [
  { value: 'staff',   label: 'Care team' },
  { value: 'patient', label: 'Patient' },
  { value: '',          label: 'All' },
];

const ROLE_OPTIONS = ALL_ROLES.map(r => ({ value: r, label: ROLE_LABEL[r] }));

const STATUS_OPTIONS = [
  { value: 'active',    label: 'Active' },
  { value: 'completed', label: 'Done' },
  { value: 'missed',    label: 'Missed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function FilterBar({
  filterAssignee, setFilterAssignee,
  ownerRoles, setOwnerRoles,
  domains, setDomains,
  domainOptions,
  statuses, setStatuses,
  highOnly, setHighOnly,
  filteredCount, totalCount,
  onReset,
}) {
  const dirty =
       filterAssignee !== ''
    || ownerRoles.size !== ALL_ROLES.length
    || (domains !== null && (domains.size !== domainOptions.length))
    || statuses.size !== DEFAULT_STATUSES.length
    || !DEFAULT_STATUSES.every(s => statuses.has(s))
    || highOnly;

  const domainSelected = domains || new Set(domainOptions);
  const domainSelectOptions = domainOptions.map(d => ({
    value: d,
    label: d,
    swatch: DOMAIN_COLORS[d] || '#6B7280',
  }));

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
      padding: '8px 0 12px', borderBottom: '1px solid var(--wi-divider)', marginBottom: 12,
    }}>
      <SingleSelect
        label="Assignee"
        options={ASSIGNEE_OPTIONS}
        value={filterAssignee}
        onChange={setFilterAssignee}
      />
      <MultiSelect
        label="Role"
        options={ROLE_OPTIONS}
        selected={ownerRoles}
        onChange={setOwnerRoles}
      />
      <MultiSelect
        label="Status"
        options={STATUS_OPTIONS}
        selected={statuses}
        onChange={setStatuses}
      />
      {domainOptions.length > 0 && (
        <MultiSelect
          label="Domain"
          options={domainSelectOptions}
          selected={domainSelected}
          onChange={setDomains}
        />
      )}
      <HighOnlyToggle active={highOnly} onClick={() => setHighOnly(!highOnly)} />

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="detail1 text-secondary" style={{ fontSize: 11 }}>
          {filteredCount} of {totalCount}
        </span>
        {dirty && (
          <button
            onClick={onReset}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              fontSize: 11, fontWeight: 500, color: 'var(--wi-action-primary)',
              fontFamily: 'var(--wi-font-family)',
            }}
          >
            Reset filters
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Main modal ───────────────────────────────────────────────── */

export default function TaskCalendarModal({ patient, onClose }) {
  const [data, setData] = useState(null);   // { openTasks, closedTasks, ... } | null = loading
  const [loadAllRunning, setLoadAllRunning] = useState(false);
  // Calendar default is 'all assignees' (server-side) — the calendar is meant
  // to show the full picture of what's happening for a patient across time,
  // including patient self-care actions (BP readings, content, intakes). The
  // List view (TaskHistoryModal) keeps its 'staff' default since it's a
  // triage view.
  const [filterAssignee, setFilterAssignee] = useState('');
  const [ownerRoles, setOwnerRoles] = useState(() => new Set(ALL_ROLES));
  // null = "auto-select all domains as they appear in data" (default).
  // Becomes a Set once the user explicitly checks/unchecks anything in the dropdown.
  const [domains, setDomains] = useState(null);
  const [statuses, setStatuses] = useState(() => new Set(DEFAULT_STATUSES));
  const [highOnly, setHighOnly] = useState(false);

  const [view, setView] = useState('month'); // 'month' | 'week'
  const [cursor, setCursor] = useState(() => startOfDay(new Date()));
  const [hover, setHover] = useState(null); // { task, rect } | null
  const [overflow, setOverflow] = useState(null); // { date, tasks } | null

  const fetchTaskHistory = useFetchTaskHistory();

  // Pull ALL tasks (open + every closed page) — per-patient volumes are bounded.
  useEffect(() => {
    let cancelled = false;
    setData(null);
    setLoadAllRunning(true);
    (async () => {
      try {
        const first = await fetchTaskHistory(patient.userid, {
          assignedToType: filterAssignee || undefined,
        });
        if (cancelled) return;
        if (!first.hasMoreClosed) {
          setData(first);
          return;
        }
        setData(first);
        let acc = [...first.closedTasks];
        let offset = first.closedOffset + acc.length;
        let more = first.hasMoreClosed;
        while (more && !cancelled) {
          const next = await fetchTaskHistory(patient.userid, {
            closedOffset: offset,
            closedLimit: first.closedLimit,
            assignedToType: filterAssignee || undefined,
          });
          if (cancelled) return;
          acc = [...acc, ...next.closedTasks];
          offset += next.closedTasks.length;
          more = next.hasMoreClosed;
        }
        if (!cancelled) {
          setData(prev => ({
            ...(prev || first),
            openTasks: first.openTasks,
            closedTasks: acc,
            closedTotal: acc.length,
            hasMoreClosed: false,
          }));
        }
      } finally {
        if (!cancelled) setLoadAllRunning(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patient.userid, filterAssignee, fetchTaskHistory]);

  // Body scroll + Esc close + keyboard nav
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === 'Escape') {
        if (overflow) setOverflow(null);
        else if (hover) setHover(null);
        else onClose();
        return;
      }
      // Don't hijack typing inside form inputs
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); shift(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); shift(1); }
      if (e.key === 't' || e.key === 'T') { e.preventDefault(); setCursor(startOfDay(new Date())); }
    }
    function shift(dir) {
      setCursor(prev => {
        if (view === 'month') {
          const next = new Date(prev);
          next.setMonth(next.getMonth() + dir);
          return next;
        }
        return addDays(prev, 7 * dir);
      });
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [view, onClose, hover, overflow]);

  /* Combined task list + filtering (excluding the date filter — calendar
     handles date placement directly) */
  const allTasks = useMemo(() => {
    if (!data) return [];
    return [...(data.openTasks || []), ...(data.closedTasks || [])];
  }, [data]);

  const domainOptions = useMemo(() => {
    const set = new Set();
    for (const t of allTasks) if (t.clinical_domain) set.add(t.clinical_domain);
    return [...set].sort();
  }, [allTasks]);

  // When all canonical roles are selected, treat as "no filter" — including
  // tasks with non-canonical owner_role values like `rn`, `admin`, `ops` or
  // null, which exist in legacy data. Only filter strictly when the user has
  // narrowed to a subset.
  const allRolesSelected = ownerRoles.size === ALL_ROLES.length
    && ALL_ROLES.every(r => ownerRoles.has(r));

  const filtered = useMemo(() => {
    return allTasks.filter(t => {
      // Status: 'active' bucket = pending/in_progress/scheduled/overdue
      const isActive = OPEN_STATUSES.has(t.status);
      const statusBucket = isActive ? 'active' : t.status;
      if (!statuses.has(statusBucket)) return false;
      if (!allRolesSelected && !ownerRoles.has(t.owner_role)) return false;
      // domains: null = no filter (all selected by default); Set = filter to those
      if (domains !== null && !domains.has(t.clinical_domain)) return false;
      if (highOnly && t.priority !== 'urgent' && t.priority !== 'high') return false;
      return true;
    });
  }, [allTasks, statuses, ownerRoles, domains, highOnly, allRolesSelected]);

  /* Group by anchor date */
  const tasksByDay = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const d = anchorTaskDate(t);
      if (!d) continue;
      const k = dateKey(d);
      const arr = map.get(k);
      if (arr) arr.push(t);
      else map.set(k, [t]);
    }
    // Stable order within a day: urgent -> high -> normal -> low; within each, open first.
    const PRI = { urgent: 0, high: 1, normal: 2, low: 3 };
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const pa = PRI[a.priority] ?? 4;
        const pb = PRI[b.priority] ?? 4;
        if (pa !== pb) return pa - pb;
        const oa = OPEN_STATUSES.has(a.status) ? 0 : 1;
        const ob = OPEN_STATUSES.has(b.status) ? 0 : 1;
        if (oa !== ob) return oa - ob;
        return new Date(b.modified ?? b.updated_at ?? 0) - new Date(a.modified ?? a.updated_at ?? 0);
      });
    }
    return map;
  }, [filtered]);

  /* Compute the visible grid */
  const today = startOfDay(new Date());
  const monthDays = useMemo(() => {
    if (view !== 'month') return [];
    const monthStart = startOfMonth(cursor);
    const monthEnd = endOfMonth(cursor);
    const gridStart = startOfWeek(monthStart);
    const lastWeekStart = startOfWeek(monthEnd);
    const gridEnd = addDays(lastWeekStart, 6);
    const days = [];
    for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) {
      days.push(new Date(d));
    }
    return days;
  }, [view, cursor]);
  const weekDays = useMemo(() => {
    if (view !== 'week') return [];
    const start = startOfWeek(cursor);
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [view, cursor]);

  function resetFilters() {
    setFilterAssignee('');
    setOwnerRoles(new Set(ALL_ROLES));
    setDomains(null);
    setStatuses(new Set(DEFAULT_STATUSES));
    setHighOnly(false);
  }

  function handleHover(task, rect) { setHover({ task, rect }); }
  function handleLeave() { setHover(null); }

  const counts = useMemo(() => {
    if (!data) return { open: 0, closed: 0 };
    return { open: data.openTasks?.length || 0, closed: data.closedTasks?.length || 0 };
  }, [data]);

  const headerLabel = view === 'month' ? formatMonthYear(cursor) : formatWeekRange(startOfWeek(cursor));

  // Tasks in the currently-visible window (vs total `filtered`). Drives the
  // empty-window hint so future-pointed views explain themselves.
  const visibleDays = view === 'month' ? monthDays : weekDays;
  const windowTaskCount = useMemo(() => {
    let n = 0;
    for (const d of visibleDays) n += (tasksByDay.get(dateKey(d))?.length || 0);
    return n;
  }, [visibleDays, tasksByDay]);

  const visibleStart = visibleDays[0];
  const visibleEnd   = visibleDays[visibleDays.length - 1];
  const windowIsFuture = visibleStart && visibleStart > today;

  // Tasks in the visible window that are filtered OUT only by the Status
  // filter — drives the "X hidden cancelled tasks" hint so the user can opt
  // in without us auto-showing them.
  const hiddenByStatusInWindow = useMemo(() => {
    if (!visibleStart || !visibleEnd) return { count: 0, statuses: new Set() };
    const start = visibleStart;
    const end = addDays(visibleEnd, 1);
    let n = 0;
    const seen = new Set();
    for (const t of allTasks) {
      const a = anchorTaskDate(t);
      if (!a || a < start || a >= end) continue;
      const isActive = OPEN_STATUSES.has(t.status);
      const statusBucket = isActive ? 'active' : t.status;
      if (statuses.has(statusBucket)) continue; // already visible (or hidden by another filter)
      if (!allRolesSelected && !ownerRoles.has(t.owner_role)) continue;
      if (domains !== null && !domains.has(t.clinical_domain)) continue;
      if (highOnly && t.priority !== 'urgent' && t.priority !== 'high') continue;
      n++;
      seen.add(statusBucket);
    }
    return { count: n, statuses: seen };
  }, [allTasks, statuses, ownerRoles, domains, highOnly, allRolesSelected, visibleStart, visibleEnd]);

  return createPortal((
    <div className="modal-overlay" onClick={onClose}>
      <style>{`
        @keyframes taskcal-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.45; transform: scale(0.85); }
        }
      `}</style>
      <div
        className="modal-content"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 1100, width: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div className="modal-header" style={{ alignItems: 'flex-start' }}>
          <div>
            <h2 className="header3" style={{ margin: 0 }}>Task Calendar</h2>
            <p className="detail1 text-secondary" style={{ margin: '4px 0 0', textTransform: 'capitalize' }}>
              {patient.firstname} {patient.lastname}
              {data && (
                <>
                  {' '}&mdash; <strong style={{ color: 'var(--wi-text-primary)' }}>{counts.open} open</strong>
                  {' · '}{counts.closed} closed
                  {loadAllRunning && (
                    <span style={{ marginLeft: 6, color: 'var(--wi-text-placeholder)' }}>· loading more…</span>
                  )}
                </>
              )}
            </p>
            <p className="detail1 text-secondary" style={{ margin: '2px 0 0', fontSize: 10, color: 'var(--wi-text-placeholder)' }}>
              Open tasks on the date they become actionable · done tasks on the day completed
            </p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {/* Navigation row */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8,
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              onClick={() => {
                if (view === 'month') {
                  const n = new Date(cursor); n.setMonth(n.getMonth() - 1); setCursor(n);
                } else setCursor(prev => addDays(prev, -7));
              }}
              aria-label={view === 'month' ? 'Previous month' : 'Previous week'}
              style={navBtnStyle}
            >
              ‹
            </button>
            <button
              onClick={() => setCursor(startOfDay(new Date()))}
              style={{ ...navBtnStyle, padding: '4px 12px' }}
            >
              Today
            </button>
            <button
              onClick={() => {
                if (view === 'month') {
                  const n = new Date(cursor); n.setMonth(n.getMonth() + 1); setCursor(n);
                } else setCursor(prev => addDays(prev, 7));
              }}
              aria-label={view === 'month' ? 'Next month' : 'Next week'}
              style={navBtnStyle}
            >
              ›
            </button>
          </div>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--wi-text-primary)' }}>
            {headerLabel}
          </h3>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 4, background: 'var(--wi-background-primary)', padding: 2, borderRadius: 6 }}>
            {['month', 'week'].map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                aria-pressed={view === v}
                style={{
                  background: view === v ? 'var(--wi-background-secondary)' : 'transparent',
                  color: view === v ? 'var(--wi-text-primary)' : 'var(--wi-text-secondary)',
                  border: view === v ? '1px solid var(--wi-divider)' : '1px solid transparent',
                  borderRadius: 4, padding: '3px 12px', fontSize: 12, fontWeight: 500,
                  cursor: 'pointer', fontFamily: 'var(--wi-font-family)', textTransform: 'capitalize',
                }}
              >
                {v}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        {data && allTasks.length > 0 && (
          <FilterBar
            filterAssignee={filterAssignee} setFilterAssignee={setFilterAssignee}
            ownerRoles={ownerRoles} setOwnerRoles={setOwnerRoles}
            domains={domains} setDomains={setDomains}
            domainOptions={domainOptions}
            statuses={statuses} setStatuses={setStatuses}
            highOnly={highOnly} setHighOnly={setHighOnly}
            filteredCount={filtered.length} totalCount={allTasks.length}
            onReset={resetFilters}
          />
        )}

        {/* Loading skeleton */}
        {data === null && (
          <div className="loading-center" style={{ padding: '40px 0' }}>
            <div className="loading-spinner" />
            <span className="loading-center-text">Loading task calendar…</span>
          </div>
        )}

        {/* Empty (no tasks at all) */}
        {data && allTasks.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <p className="body2 text-secondary">No tasks found for this patient</p>
          </div>
        )}

        {/* Calendar body */}
        {data && allTasks.length > 0 && (
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }} role="grid" aria-label="Task calendar">
            {view === 'month' && (
              <>
                {/* Weekday header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4,
                  marginBottom: 4,
                }}>
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
                    <div key={d} style={{
                      fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em',
                      color: i >= 5 ? 'var(--wi-text-placeholder)' : 'var(--wi-text-secondary)',
                      padding: '4px 6px',
                    }}>
                      {d}
                    </div>
                  ))}
                </div>
                {/* Cells — fixed-height rows so the grid stays regular regardless of chip count */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gridAutoRows: '116px',
                  gap: 4,
                }}>
                  {monthDays.map(d => {
                    const k = dateKey(d);
                    const dayTasks = tasksByDay.get(k) || [];
                    const isToday = isSameDay(d, today);
                    const isCurrent = d.getMonth() === cursor.getMonth();
                    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                    return (
                      <MonthCell
                        key={k}
                        date={d}
                        tasks={dayTasks}
                        isToday={isToday}
                        isCurrentMonth={isCurrent}
                        isWeekend={isWeekend}
                        onHover={handleHover}
                        onLeave={handleLeave}
                        onShowAll={(date, tasks) => setOverflow({ date, tasks })}
                      />
                    );
                  })}
                </div>
              </>
            )}

            {view === 'week' && (
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6,
              }}>
                {weekDays.map(d => {
                  const k = dateKey(d);
                  const dayTasks = tasksByDay.get(k) || [];
                  const isToday = isSameDay(d, today);
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <WeekCell
                      key={k}
                      date={d}
                      tasks={dayTasks}
                      isToday={isToday}
                      isWeekend={isWeekend}
                      onHover={handleHover}
                      onLeave={handleLeave}
                    />
                  );
                })}
              </div>
            )}

            {windowTaskCount === 0 && (
              <div style={{
                textAlign: 'center', padding: '20px 16px', marginTop: 12,
                background: 'var(--wi-background-primary)',
                borderRadius: 6,
              }}>
                {filtered.length === 0 ? (
                  <p className="body2 text-secondary" style={{ margin: 0 }}>
                    No tasks match the selected filters
                  </p>
                ) : windowIsFuture ? (
                  <>
                    <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary)', fontWeight: 500 }}>
                      No active tasks in this window yet
                    </p>
                    <p className="detail1 text-secondary" style={{ margin: '4px 0 0' }}>
                      Recurring tasks materialize closer to their due date — check back as the window approaches.
                    </p>
                  </>
                ) : (
                  <p className="body2 text-secondary" style={{ margin: 0 }}>
                    No tasks in this window
                  </p>
                )}
              </div>
            )}

            {/* Hidden-by-status hint — surfaces cancelled/missed tasks that
                exist in this window without auto-showing them. Click reveals
                them via the Status filter. */}
            {hiddenByStatusInWindow.count > 0 && (
              <div style={{
                marginTop: 8, padding: '8px 12px',
                background: 'var(--wi-background-secondary)',
                border: '1px dashed var(--wi-divider)',
                borderRadius: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                fontSize: 12, color: 'var(--wi-text-secondary)',
              }}>
                <span>
                  {hiddenByStatusInWindow.count} {[...hiddenByStatusInWindow.statuses].join(' / ')}
                  {' '}task{hiddenByStatusInWindow.count > 1 ? 's' : ''} hidden in this window
                </span>
                <button
                  onClick={() => {
                    const next = new Set(statuses);
                    for (const s of hiddenByStatusInWindow.statuses) next.add(s);
                    setStatuses(next);
                  }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    fontSize: 12, fontWeight: 500, color: 'var(--wi-action-primary)',
                    fontFamily: 'var(--wi-font-family)',
                  }}
                >
                  Show
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hover popover (rendered outside cells so it isn't clipped) */}
      {hover && <TaskPopover task={hover.task} anchorRect={hover.rect} onClose={() => setHover(null)} />}

      {/* Day overflow modal */}
      {overflow && (
        <DayOverflowPopover
          date={overflow.date}
          tasks={overflow.tasks}
          onClose={() => setOverflow(null)}
          onHover={handleHover}
          onLeave={handleLeave}
        />
      )}
    </div>
  ), document.body);
}

const navBtnStyle = {
  background: 'var(--wi-background-primary)',
  border: '1px solid var(--wi-divider)',
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 13, fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--wi-font-family)',
  color: 'var(--wi-text-primary)',
  minWidth: 32,
};
